import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode | undefined;
}

/**
 * Right-edge slide-over: nine tenths of the viewport, full height, for the
 * one surface that needs the room — the add-cards picker, which sits a facet
 * column next to a paginated table and was being squeezed into a centred box.
 *
 * A sibling of Modal, not a variant of it. Modal is the frame every other
 * dialog of the console renders through and it stays exactly as it is, so the
 * overlay pieces here (Escape listener, backdrop, panel header, scrolling body)
 * are copied at Modal's level on purpose rather than factored out of it: same
 * behaviour and same z-layering (z-40 backdrop, z-50 panel), no shared code
 * whose next edit could reach the dialogs.
 *
 * The slide-in is a keyframe animation (`co-drawer-enter`, in index.css) and
 * NOT a `translate-x-*` utility: in Tailwind 4 a translate utility composes
 * with whatever other transform is already on the element instead of replacing
 * it, so a utility next to an animated transform stacks into a double offset.
 * The keyframe owns `transform` outright and nothing else on the panel sets
 * one; `prefers-reduced-motion` drops the animation there too.
 */
export function Drawer({ open, title, onClose, children, footer }: Props) {
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="co-backdrop-enter fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* 90vw is the ask. The min-width keeps the facet column from being
          crushed on a small laptop and collapses to the viewport below that, so
          a narrow screen gets a full-width sheet rather than a panel wider than
          the screen it sits on. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="co-drawer-enter fixed right-0 top-0 z-50 flex h-[100dvh] w-[90vw] min-w-[min(100vw,720px)] flex-col overflow-hidden border-l border-[var(--co-border)] bg-[var(--co-surface)] shadow-2xl"
      >
        <div className="flex items-center justify-between gap-4 border-b border-[var(--co-border)] px-4 py-3">
          <h2 className="text-[15px] font-semibold text-[var(--co-text)]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-[var(--co-muted)] transition-colors hover:bg-[var(--co-bg)] hover:text-[var(--co-text)]"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        {footer ? (
          <div className="border-t border-[var(--co-border)] px-4 py-3">{footer}</div>
        ) : null}
      </div>
    </>
  );
}
